import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, authHeaders } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";

const GREEN = "#004225";
const GOLD = "#D4AF37";
const PIE_COLORS = ["#D4AF37", "#004225", "#085732", "#B5942D", "#555555", "#8a6d1a", "#C9C9B4", "#7A8B7A"];

const Panel = ({ title, children }) => (
  <div className="bg-white border border-[#004225]/10 rounded-sm p-5">
    <div className="font-display text-lg text-[#004225] mb-4">{title}</div>
    {children}
  </div>
);

const Empty = () => <div className="text-sm text-[#171717]/50 py-8 text-center">Not enough data yet</div>;

const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, authHeaders()).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-[#171717]/60 text-sm">Loading analytics…</div>;

  const statusData = Object.entries(stats.by_status || {}).map(([name, value]) => ({ name, value }));
  const countryData = (stats.by_country || []).slice(0, 10);
  const productData = (stats.top_products || []).slice(0, 8);
  const monthData = stats.by_month || [];

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-analytics">
      <Panel title="Enquiries by Country">
        {countryData.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={countryData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="country" width={90} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill={GREEN} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>
      <Panel title="Status Distribution">
        {statusData.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={95} label={{ fontSize: 11 }}>
                {statusData.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Panel>
      <Panel title="Enquiries by Month">
        {monthData.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={GOLD} strokeWidth={2.5} dot={{ fill: GREEN }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>
      <Panel title="Enquiries by Product">
        {productData.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={productData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill={GOLD} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
};

export default AdminAnalytics;
